import { Badge, Box, Button, ButtonGroup, BadgeProps, Skeleton } from "@chakra-ui/react";
import { CollecticonMap, CollecticonTickSmall, CollecticonXmarkSmall, CollecticonFlagPole } from "@devseed-ui/collecticons-chakra";

import { Loading, Overline, SceneThumbnail, ConfidenceTag } from ".";
import { Inference } from "../types/inferences";
import { formatDate, getLikelySpecies, getSpeciesColor, getSpeciesLabel } from "../utils";
import { useModel } from "../context/AppConfig";
import { Link } from "react-router-dom";

type DetectionInfoProps = {
  label: string;
  value: string | number;
  valueAsTag?: boolean | null;
}

function DetectionInfo({label, value, valueAsTag}: DetectionInfoProps) {
  return (
    <Box display="flex" as="dl" alignItems="center" gap="4">
      <Overline as="dt" fontWeight="500" opacity="0.675" flex="1">{label}</Overline>
      {valueAsTag ? <ConfidenceTag value={value} /> : <Box as="dd">{value}</Box>}
    </Box>
  );
}

type ValidatedBadgeProps = {
  isValid: boolean | null;
} & BadgeProps
export function ValidatedBadge({ isValid, ...props }: ValidatedBadgeProps) {
  return (
    <Badge
      zIndex="1000"
      variant="subtle"
      colorScheme={isValid ? "green" : "red"}
      size="sm"
      {...props}
    >
      {isValid ? "Valid" : "Invalid"}
    </Badge>
  );
}

type Props = {
  inference: Inference;
  validate?: (options: {
    validationId: number;
    isValid: boolean;
    compositeIds?: number[];
  }) => void;
  isValidating?: boolean;
  includeThumbnail?: boolean;
  includeDate?: boolean;
  linkToValidate?: boolean;
  compositeMode?: boolean;
  brightness?: number;
  contrast?: number;
}

function DetectionCard({ inference, validate, isValidating, includeThumbnail, includeDate, brightness, contrast, linkToValidate, compositeMode}: Props) {
  const { id, composite_id, ids, inference_ids, is_valid, scene, model_id, flagged } = inference.properties;
  const model = useModel(model_id);
  const { species, confidence } = getLikelySpecies(inference);

  const isValidated = is_valid !== undefined && is_valid !== null;

  // In composite mode, the URL anchor is the first member inference_ids[0] (or ids[0]),
  // matching getAnchorId in useValidateNavigation.
  // In non-composite mode, use id.
  const navigationId = compositeMode
    ? (inference_ids?.[0] ?? ids?.[0])
    : id;
  const validationId = composite_id ?? id;

  if (!navigationId) {
    // eslint-disable-next-line no-console
    console.error("DetectionCard: no valid ID found in inference properties", inference);
    return null;
  }

  return (
    <Box fontSize="sm">
      {linkToValidate &&
        <Box as={Link} to={`/scenes/${scene.s3_prefix}/validate/${navigationId}`} state={{ compositeMode: compositeMode ?? true }} position="absolute" left="2" top="2" color="primary.200" zIndex="10" _hover={{ color: "primary.400" }} transition="all 0.32s ease-out" cursor="pointer" title="Validate detection in map">
          <CollecticonMap />
        </Box>}
      {flagged && linkToValidate && (
        <Box position="absolute" left="8" top="2" color="orange.400" zIndex="10" title="Flagged detection">
          <CollecticonFlagPole />
        </Box>
      )}
      {includeThumbnail && (
        <>
          {isValidated &&
          <ValidatedBadge
            isValid={is_valid}
            position="absolute"
            right="2"
            top="2"
          />}
          <SceneThumbnail width={250} height={120} bbox={inference.bbox} sceneId={scene.s3_prefix} brightness={brightness ?? 1} contrast={contrast ?? 1} />
        </>
      )}
      <Box px="3" my="3" display="flex" flexDir="column" gap="1">
        {includeDate && <DetectionInfo label="Date" value={formatDate(new Date(Date.parse(scene.date))).split(",")[0]} />}
        <Box display="flex" as="dl" alignItems="center" gap="1">
          <Overline as="dt" fontWeight="500" opacity="0.675" flex="1">Class</Overline>
          <Box
            bgColor={getSpeciesColor(species)}
            borderRadius="full"
            height="3"
            minWidth="3"
          />
          <Box as="dd">{getSpeciesLabel(species)}</Box>
        </Box>
        <DetectionInfo label="Confidence" value={confidence} valueAsTag />
        {model && <DetectionInfo label="Model" value={model?.name || ""} />}
      </Box>
      {isValidating && !isValidated && (
        <Loading
          display="flex"
          gap="2"
          justifyContent="center"
          fontSize="sm"
          opacity="0.75"
        >
          Updating validation
        </Loading>
      )}
      {validate && !isValidating && !isValidated && validationId && (
        <ButtonGroup size="sm" px="3" pb="3" isAttached width="100%">
          <Button
            flex="1"
            aria-label="Reject detection"
            title="Reject detection"
            colorScheme="red"
            variant="ghost"
            onClick={() => validate({ validationId, isValid: false, compositeIds: ids })}
            isDisabled={isValidating}
          >
            <CollecticonXmarkSmall />
          </Button>
          <Button
            flex="1"
            aria-label="Confirm detection"
            title="Confirm detection"
            colorScheme="green"
            variant="solid"
            onClick={() => validate({ validationId, isValid: true, compositeIds: ids })}
            isDisabled={isValidating}
          >
            <CollecticonTickSmall />
          </Button>
        </ButtonGroup>
      )}
    </Box>
  );
}

export default DetectionCard;

export function LoadingDetectionCard({ noData }: { noData?: boolean }) {
  return (
    <Box fontSize="sm">
      <Skeleton height="120px" speed={noData ? 1000 : 0.8} />
      <Box px="3" my="3" display="flex" flexDir="column" gap="2">
        <Box display="flex" justifyContent="space-between">
          <Skeleton height="3" width="16" speed={noData ? 1000 : 0.8} />
          <Skeleton height="3" width="24" speed={noData ? 1000 : 0.8} />
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Skeleton height="3" width="10" speed={noData ? 1000 : 0.8} />
          <Skeleton height="3" width="8" speed={noData ? 1000 : 0.8} />
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Skeleton height="3" width="12" speed={noData ? 1000 : 0.8} />
          <Skeleton height="3" width="16" speed={noData ? 1000 : 0.8} />
        </Box>
      </Box>
    </Box>
  );
}
